import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings, User, Heart, Save, AlertTriangle, IdCard, Globe, Shield, Copy, Link2, Unlink } from 'lucide-react';
import { patientApi } from '../api';
import type { PatientProfile, EmergencyProfile } from '../types';
import PatientAvatar from '../components/PatientAvatar';
import Spinner from '../../../components/ui/Spinner';
import { useToast } from '../../../components/ui/Toast';

type Tab = 'profile' | 'emergency' | 'healthids';
const TAB_KEYS: Tab[] = ['profile', 'emergency', 'healthids'];

export default function PatientSettings() {
  const toast = useToast();
  // Deep-linkable: ?tab=profile|emergency|healthids opens that tab, and
  // ?focus=<field> scrolls to + focuses a specific input (used by the
  // "Edit" buttons on Medical Basics).
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const focusField = searchParams.get('focus');
  const [tab, setTab] = useState<Tab>(tabParam && TAB_KEYS.includes(tabParam) ? tabParam : 'profile');
  const [profile, setProfile] = useState<Partial<PatientProfile>>({});
  const [emergency, setEmergency] = useState<Partial<EmergencyProfile>>({});
  const [photoBusy, setPhotoBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [abhaId, setAbhaId] = useState('');
  const [abdmStatus, setAbdmStatus] = useState('NOT_LINKED');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    if (tab === 'profile' || tab === 'healthids') {
      patientApi.getProfile().then(d => {
        if (alive) {
          setProfile(d || {});
          setAbhaId(d?.abhaId || '');
          setAbdmStatus(d?.abhaStatus || '');
        }
      }).catch((e: any) => {
        // A silent swallow here would leave the form blank with no
        // indication anything went wrong — indistinguishable from actual
        // data loss. Surface it instead.
        if (alive) toast.error(e.message || 'Could not load your profile.');
      }).finally(() => { if (alive) setLoading(false); });
    } else {
      patientApi.getEmergencyProfile().then(d => { if (alive) setEmergency(d || {}); })
        .catch((e: any) => {
          if (alive) toast.error(e.message || 'Could not load your emergency info.');
        }).finally(() => { if (alive) setLoading(false); });
    }
    return () => { alive = false; };
  }, [tab, toast]);

  useEffect(() => {
    if (loading || !focusField) return;
    const el = document.getElementById(`field-${focusField}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (el.querySelector('input, textarea, select') as HTMLElement | null)?.focus();
    }
  }, [loading, focusField]);

  const save = async (fn: () => Promise<any>, successMessage = 'Saved successfully') => {
    setSaving(true);
    try {
      await fn();
      toast.success(successMessage);
    } catch (e: any) {
      toast.error(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const SaveBtn = ({ onClick }: { onClick: () => void }) => (
    <button onClick={onClick} disabled={saving} className="btn-primary flex items-center gap-2">
      {saving ? <Spinner size="sm" /> : <><Save className="w-4 h-4" /> Save</>}
    </button>
  );

  const TABS: { key: Tab; label: string; icon: typeof User }[] = [
    { key: 'profile', label: 'Profile', icon: User },
    { key: 'emergency', label: 'Emergency', icon: Heart },
    { key: 'healthids', label: 'Health IDs', icon: IdCard },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="w-5 h-5 text-gray-400" /> Settings
        </h1>
        <p className="text-sm text-gray-500 mt-1">Manage your profile, emergency info, and health IDs</p>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
              tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : tab === 'profile' ? (
        <div className="card p-5 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-gray-500 font-medium">NeoHealthCard ID</p>
              <p className="text-sm font-mono font-bold text-teal-700">{profile.nhcId || '—'}</p>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(profile.nhcId || ''); }}
              className="p-1.5 text-gray-400 hover:text-teal-600 rounded-lg hover:bg-gray-100" title="Copy NHC ID">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          {/* The photo. Three endpoints existed for it and this app used none,
              so a photo set in the app was invisible here and unchangeable. */}
          <div className="flex items-center gap-4">
            <PatientAvatar avatarUrl={profile.avatarUrl} name={profile.fullName} size={64} />
            <div className="flex flex-col gap-1.5">
              <label className={`text-xs font-semibold ${photoBusy ? 'text-gray-400' : 'text-teal-700 cursor-pointer hover:text-teal-800'}`}>
                {photoBusy ? 'Working…' : profile.avatarUrl ? 'Change photo' : 'Add a photo'}
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  disabled={photoBusy}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (!f) return;
                    setPhotoBusy(true);
                    try {
                      setProfile(await patientApi.uploadProfilePhoto(f));
                      toast.success('Photo updated');
                    } catch {
                      // A photo the server refused must not look accepted.
                      toast.error('That photo could not be uploaded — JPG or PNG only.');
                    } finally { setPhotoBusy(false); }
                  }}
                />
              </label>
              {profile.avatarUrl && (
                <button
                  disabled={photoBusy}
                  onClick={async () => {
                    if (!confirm('Remove your profile photo?')) return;
                    setPhotoBusy(true);
                    try {
                      setProfile(await patientApi.deleteProfilePhoto());
                      toast.success('Photo removed');
                    } catch {
                      toast.error('The photo could not be removed.');
                    } finally { setPhotoBusy(false); }
                  }}
                  className="text-xs text-red-600 hover:text-red-700 font-medium text-left disabled:opacity-50"
                >
                  Remove photo
                </button>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name</label>
              <input className="input" value={profile.fullName || ''} onChange={e => setProfile(p => ({ ...p, fullName: e.target.value }))} />
            </div>
            <div>
              <label className="label">Phone</label>
              <input className="input" value={profile.phone || ''} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} />
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" value={profile.email || ''} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div>
              <label className="label">Date of Birth</label>
              <input className="input" type="date" value={profile.dateOfBirth?.split('T')[0] || ''} onChange={e => setProfile(p => ({ ...p, dateOfBirth: e.target.value }))} />
            </div>
            <div>
              <label className="label">Gender</label>
              <select className="input" value={profile.gender || ''} onChange={e => setProfile(p => ({ ...p, gender: e.target.value }))}>
                <option value="">Select</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div id="field-bloodGroup">
              <label className="label">Blood Group</label>
              <input className="input" placeholder="e.g. O+" value={profile.bloodGroup || ''} onChange={e => setProfile(p => ({ ...p, bloodGroup: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="label">Address</label>
            <textarea className="input" rows={2} value={profile.address || ''} onChange={e => setProfile(p => ({ ...p, address: e.target.value }))} />
          </div>
          <div id="field-allergies">
            <label className="label">Known Allergies</label>
            <input className="input" placeholder="Comma separated" value={(profile.allergies || []).join(', ')} onChange={e => setProfile(p => ({ ...p, allergies: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} />
          </div>
          <div id="field-conditions">
            <label className="label">Chronic Conditions</label>
            <input className="input" placeholder="Comma separated" value={(profile.chronicConditions || []).join(', ')} onChange={e => setProfile(p => ({ ...p, chronicConditions: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} />
          </div>
          {/* The server holds this as one free-text string, which is why it is
              one field and not a comma-split list. It was mapped to a hardcoded
              empty array, so whatever the patient wrote was never shown. */}
          <div id="field-medicines">
            <label className="label">Medicines You Take</label>
            <textarea
              className="input"
              rows={2}
              placeholder="e.g. Amlodipine 5mg every morning; Atorvastatin 10mg at night"
              value={profile.medicines || ''}
              onChange={e => setProfile(p => ({ ...p, medicines: e.target.value }))}
            />
          </div>
          <div id="field-surgeries">
            <label className="label">Past Surgeries</label>
            <input className="input" placeholder="Comma separated — e.g. Appendectomy (2019)" value={(profile.surgeries || []).join(', ')} onChange={e => setProfile(p => ({ ...p, surgeries: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} />
          </div>
          <div id="field-familyHistory">
            <label className="label">Family Medical History</label>
            <input className="input" placeholder="Comma separated — e.g. Diabetes — Father, Hypertension — Mother" value={(profile.familyHistory || []).join(', ')} onChange={e => setProfile(p => ({ ...p, familyHistory: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} />
          </div>
          <SaveBtn onClick={() => save(() => patientApi.updateProfile(profile), 'Profile updated')} />
        </div>
      ) : tab === 'emergency' ? (
        <div className="card p-5 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex gap-2 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            This information is shared with ambulance crews during emergencies.
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Emergency Contact Name</label>
              <input className="input" value={emergency.emergencyContactName || ''} onChange={e => setEmergency(p => ({ ...p, emergencyContactName: e.target.value }))} />
            </div>
            <div>
              <label className="label">Emergency Contact Phone</label>
              <input className="input" type="tel" inputMode="numeric" maxLength={10} value={emergency.emergencyContactPhone || ''} onChange={e => setEmergency(p => ({ ...p, emergencyContactPhone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} />
            </div>
            <div>
              <label className="label">Secondary Contact Name</label>
              <input className="input" value={emergency.secondaryContactName || ''} onChange={e => setEmergency(p => ({ ...p, secondaryContactName: e.target.value }))} />
            </div>
            <div>
              <label className="label">Secondary Contact Phone</label>
              <input className="input" type="tel" inputMode="numeric" maxLength={10} value={emergency.secondaryContactPhone || ''} onChange={e => setEmergency(p => ({ ...p, secondaryContactPhone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} />
            </div>
          </div>
          <div>
            <label className="label">Preferred Hospital</label>
            <input className="input" value={emergency.preferredHospital || ''} onChange={e => setEmergency(p => ({ ...p, preferredHospital: e.target.value }))} placeholder="Preferred hospital name" />
          </div>
          <div>
            <label className="label">Insurance Provider</label>
            <input className="input" value={emergency.insuranceProvider || ''} onChange={e => setEmergency(p => ({ ...p, insuranceProvider: e.target.value }))} />
          </div>
          <div>
            <label className="label">Insurance Policy Number</label>
            <input className="input" value={emergency.insurancePolicyNumber || ''} onChange={e => setEmergency(p => ({ ...p, insurancePolicyNumber: e.target.value }))} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={emergency.organDonor || false} onChange={e => setEmergency(p => ({ ...p, organDonor: e.target.checked }))} className="accent-teal-600" />
            <span className="text-sm text-gray-700">Organ Donor</span>
          </label>
          <SaveBtn onClick={() => save(() => patientApi.updateEmergencyProfile(emergency), 'Emergency info updated')} />
        </div>
      ) : (
        <div className="space-y-4">
          {/* NHC ID */}
          <div className="card p-5">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3">
              <IdCard className="w-4 h-4 text-teal-600" /> NeoHealthCard ID
            </h3>
            <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
              <p className="text-base font-mono font-bold text-teal-700">{profile.nhcId || '—'}</p>
              <button onClick={() => { navigator.clipboard.writeText(profile.nhcId || ''); }}
                className="p-1.5 text-gray-400 hover:text-teal-600 rounded-lg hover:bg-gray-100" title="Copy">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">Your unique NeoHealthCard identifier — used across all modules</p>
          </div>

          {/* ABHA ID */}
          <div className="card p-5 space-y-3">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-600" /> ABHA Number (Ayushman Bharat Health Account)
            </h3>
            <div>
              <label className="label">ABHA ID (14-digit)</label>
              <input className="input font-mono" value={abhaId} onChange={e => setAbhaId(e.target.value)}
                placeholder="XX-XXXX-XXXX-XXXX" maxLength={17} />
              <p className="text-[10px] text-gray-400 mt-0.5">Format: 14 digits (e.g. 91-1234-5678-9012)</p>
            </div>
            <SaveBtn onClick={() => save(() => patientApi.updateProfile({ abhaId }), 'ABHA ID saved')} />
          </div>

          {/* ABDM Linking */}
          <div className="card p-5 space-y-3">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-emerald-600" /> ABDM Linking Status
            </h3>
            <div className="flex items-center gap-3">
              {/*
                * Linked-ness is whether an ABHA id is actually on the account —
                * a fact — not a status string whose spelling this page has to
                * guess. It compared against 'LINKED' while the server stores
                * lowercase, so a linked account read "Not Linked"; and the
                * status it does store is shown verbatim beside it rather than
                * squeezed into three labels of this page's own invention.
                */}
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                abhaId.trim() ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {abhaId.trim() ? 'Linked' : 'Not linked'}
              </span>
              {abdmStatus && (
                <span className="text-[11px] text-gray-500">{abdmStatus}</span>
              )}
              {abhaId.trim() ? (
                <button onClick={() => {
                  if (!confirm('Unlink your ABDM account? You can re-link later.')) return;
                  /**
                   * Clearing the id IS unlinking. This used to send
                   * `abdmLinkedStatus`, a key the server does not have — zod
                   * stripped it, the PATCH returned 200, this page said
                   * "unlinked", and the account stayed linked.
                   */
                  setAbhaId('');
                  setAbdmStatus('');
                  save(() => patientApi.updateProfile({ abhaId: null, abhaStatus: null }), 'ABDM account unlinked');
                }} className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1">
                  <Unlink className="w-3.5 h-3.5" /> Unlink ABDM
                </button>
              ) : (
                <button onClick={() => {
                  toast.info('ABDM linking is a demo placeholder. Real integration requires ABDM API credentials and M3 gateway access.');
                }} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                  <Link2 className="w-3.5 h-3.5" /> Link ABDM Account
                </button>
              )}
            </div>

            <div className="bg-teal-50 border border-teal-200 rounded-lg p-2.5 flex gap-2 text-[10px] text-teal-800">
              <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <p>ABDM linking allows importing your health records from the Ayushman Bharat Digital Mission network. Your consent is required for every import. This is a demo placeholder — real ABDM integration requires API credentials.</p>
            </div>

            {abdmStatus === 'LINKED' && (
              <button onClick={() => toast.info('ABDM record import is a demo placeholder.')}
                className="px-4 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" /> Import Records from ABDM
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
